import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import PageShell from './components/PageShell';
import Snackbar from './components/Snackbar';
import { subscriptionAdminApi, getApiErrorMessage } from '../subscriptionAdmin/api';
import { canWrite } from '../subscriptionAdmin/permissions';
import { formatInr } from '../subscriptionAdmin/razorpay';
import {
  emptyPlanForm,
  formToPlanPayload,
  planBasePrice,
  planHours,
  planStatus,
  planToForm,
} from '../subscriptionAdmin/planUtils';

export default function PlansPage() {
  const [tab, setTab] = useState(0);
  const planCategory = tab === 0 ? 'main' : 'addon';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyPlanForm('main'));
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'error' });

  const canEdit = canWrite('plans');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await subscriptionAdminApi.listPlans({ planCategory });
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [planCategory]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyPlanForm(planCategory));
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditingId(plan._id);
    setForm(planToForm(plan));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.planName.trim()) {
      setSnackbar({ open: true, type: 'error', message: 'Plan name is required.' });
      return;
    }
    setSaving(true);
    try {
      const payload = formToPlanPayload({ ...form, planCategory });
      if (editingId) {
        await subscriptionAdminApi.updatePlan(editingId, payload);
        setSnackbar({ open: true, type: 'success', message: 'Plan updated.' });
      } else {
        await subscriptionAdminApi.createPlan(payload);
        setSnackbar({ open: true, type: 'success', message: 'Plan created.' });
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await subscriptionAdminApi.setPlanStatus(id, status);
      setSnackbar({ open: true, type: 'success', message: `Plan marked ${status}.` });
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  const deprecate = async (id) => {
    if (!window.confirm('Mark this plan as deprecated?')) return;
    try {
      await subscriptionAdminApi.deletePlan(id);
      setSnackbar({ open: true, type: 'success', message: 'Plan deprecated.' });
      load();
    } catch (err) {
      setSnackbar({ open: true, type: 'error', message: getApiErrorMessage(err) });
    }
  };

  return (
    <PageShell
      title="Step 1 — Master plans"
      subtitle="GET /plans-list · POST /plans-create · PUT /plans-put/:id · PATCH /plans-patch/:id/status"
      actions={
        canEdit ? (
          <Button variant="contained" onClick={openCreate}>
            New {planCategory === 'main' ? 'main' : 'add-on'} plan
          </Button>
        ) : null
      }
    >
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Main plans" />
        <Tab label="Add-on plans" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Hours/mo</TableCell>
                <TableCell>Base (INR)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748b' }}>
                    No {planCategory} plans yet. Create one before drafting contracts.
                  </TableCell>
                </TableRow>
              )}
              {plans.map((p) => (
                <TableRow key={p._id}>
                  <TableCell>{p.planName}</TableCell>
                  <TableCell>{p.planType}</TableCell>
                  <TableCell>{planHours(p)}</TableCell>
                  <TableCell>{formatInr(planBasePrice(p))}</TableCell>
                  <TableCell>
                    <Chip
                      label={planStatus(p)}
                      size="small"
                      color={planStatus(p) === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {canEdit && (
                      <>
                        <Button size="small" onClick={() => openEdit(p)}>
                          Edit
                        </Button>
                        {planStatus(p) === 'draft' && (
                          <Button size="small" onClick={() => setStatus(p._id, 'active')}>
                            Activate
                          </Button>
                        )}
                        {planStatus(p) !== 'deprecated' && (
                          <Button size="small" color="warning" onClick={() => deprecate(p._id)}>
                            Deprecate
                          </Button>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit plan' : 'Create plan'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Plan name"
            value={form.planName}
            onChange={(e) => setForm({ ...form, planName: e.target.value })}
            required
          />
          <TextField
            label="Plan type (SKU label)"
            value={form.planType}
            onChange={(e) => setForm({ ...form, planType: e.target.value })}
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            rows={2}
          />
          <TextField
            label="Base price (INR)"
            type="number"
            value={form.basePrice}
            onChange={(e) => setForm({ ...form, basePrice: e.target.value })}
            required
          />
          <TextField
            label="Discount %"
            type="number"
            value={form.discountPercent}
            onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
          />
          <TextField
            label="Sessions per month"
            type="number"
            value={form.sessionsPerMonth}
            onChange={(e) => setForm({ ...form, sessionsPerMonth: e.target.value })}
          />
          <TextField
            label="Hours per session"
            type="number"
            value={form.hoursPerSession}
            onChange={(e) => setForm({ ...form, hoursPerSession: e.target.value })}
          />
          <TextField
            label="Hours per month (override)"
            type="number"
            value={form.hoursPerMonth}
            onChange={(e) => setForm({ ...form, hoursPerMonth: e.target.value })}
          />
          <TextField
            label="Features (comma-separated)"
            value={form.featuresText}
            onChange={(e) => setForm({ ...form, featuresText: e.target.value })}
          />
          {planCategory === 'main' && (
            <>
              <TextField
                select
                label="Billing cycle"
                value={form.billingCycle}
                onChange={(e) => setForm({ ...form, billingCycle: e.target.value })}
              >
                <MenuItem value="monthly">monthly</MenuItem>
              </TextField>
              <TextField
                label="Free months template"
                type="number"
                value={form.freeMonths}
                onChange={(e) => setForm({ ...form, freeMonths: e.target.value })}
              />
            </>
          )}
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
          >
            <MenuItem value="draft">draft</MenuItem>
            <MenuItem value="active">active</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar {...snackbar} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} />
    </PageShell>
  );
}
